import { motion } from 'framer-motion'
import type { Variants } from 'framer-motion'
import { Sparkles, TrendingUp, Shield, FileText } from 'lucide-react'
import { useLanguage } from '@/context/LanguageContext'
import { useWaitlist } from '@/context/WaitlistContext'
import { EmailForm } from '@/components/ui/EmailForm'
import { Badge } from '@/components/ui/Badge'
import { Counter } from '@/components/ui/Counter'

const containerVariants: Variants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.2,
    },
  },
}

const itemVariants: Variants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.6 },
  },
}

export function Hero() {
  const { t } = useLanguage()
  const { count } = useWaitlist()

  return (
    <section id="hero" className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Background Effects */}
      <div className="absolute inset-0 grid-pattern opacity-50" />

      {/* Gradient Orbs */}
      <div className="hero-glow top-1/4 left-1/4 -translate-x-1/2 -translate-y-1/2" />
      <div className="hero-glow top-1/2 right-1/4 translate-x-1/2 -translate-y-1/2 opacity-50" />

      {/* Floating Elements */}
      <motion.div
        className="absolute top-1/4 right-[15%] hidden lg:block"
        animate={{ y: [0, -20, 0], rotate: [0, 5, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
      >
        <div className="glass-card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-profit-500/20 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-profit-400" />
          </div>
          <div>
            <p className="text-xs text-slate-400">BTC Unrealized</p>
            <p className="text-lg font-bold text-profit-400">+$4,231.50</p>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="absolute top-1/3 left-[10%] hidden lg:block"
        animate={{ y: [0, 15, 0], rotate: [0, -3, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
      >
        <div className="glass-card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gold-500/20 flex items-center justify-center">
            <Shield className="w-5 h-5 text-gold-400" />
          </div>
          <div>
            <p className="text-xs text-slate-400">ACB Calculated</p>
            <p className="text-lg font-bold text-slate-100">$12,847.32 CAD</p>
          </div>
        </div>
      </motion.div>

      <motion.div
        className="absolute bottom-1/3 right-[12%] hidden lg:block"
        animate={{ y: [0, -15, 0], rotate: [0, 3, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 2 }}
      >
        <div className="glass-card p-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-maple-500/20 flex items-center justify-center">
            <FileText className="w-5 h-5 text-maple-500" />
          </div>
          <div>
            <p className="text-xs text-slate-400">CRA Report</p>
            <p className="text-lg font-bold text-slate-100">Ready to Export</p>
          </div>
        </div>
      </motion.div>

      {/* Main Content */}
      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="max-w-4xl mx-auto text-center"
        >
          {/* Badge */}
          <motion.div variants={itemVariants} className="mb-6">
            <Badge variant="gold" className="text-sm">
              <Sparkles className="w-4 h-4" />
              {t.hero.badge}
            </Badge>
          </motion.div>

          {/* Headline */}
          <motion.h1
            variants={itemVariants}
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight mb-6"
          >
            <span className="text-slate-100">{t.hero.headline}</span>
            <br />
            <span className="gradient-text">{t.hero.headlineHighlight}</span>
            {t.hero.headlineEnd && (
              <span className="text-slate-100"> {t.hero.headlineEnd}</span>
            )}
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            variants={itemVariants}
            className="text-lg sm:text-xl text-slate-400 mb-10 max-w-2xl mx-auto"
          >
            {t.hero.subheadline}
          </motion.p>

          {/* Email Form */}
          <motion.div variants={itemVariants} className="mb-6" id="waitlist">
            <EmailForm variant="hero" />
          </motion.div>

          {/* Social Proof Counter */}
          <motion.div variants={itemVariants} className="flex items-center justify-center gap-2 text-slate-400">
            <span>{t.hero.waitlistCount.replace('{count}', '')}</span>
            <Counter value={count} className="text-gold-400 font-bold text-lg" />
            <span>+ {t.hero.trustedBy.split(' ')[0]}</span>
          </motion.div>

          {/* Trust Indicators */}
          <motion.div
            variants={itemVariants}
            className="mt-12 flex flex-wrap items-center justify-center gap-6 text-sm text-slate-500"
          >
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-profit-500" />
              <span>CRA Compliant</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-gold-500" />
              <span>256-bit Encryption</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 rounded-full bg-maple-500" />
              <span>Made in Canada</span>
            </div>
          </motion.div>
        </motion.div>
      </div>

      {/* Bottom Gradient Fade */}
      <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-midnight-950 to-transparent" />
    </section>
  )
}
